import { ReactElement, useState, useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Paper from '@material-ui/core/Paper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

import api from '~/services/api';
import { useStoreState, useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import downloadFile from '~/utils/downloadFile';
import { getFileNameFromContentDisposition } from '~/utils/headers';
import { getDevicesActions } from './helper';
import Loader from '~/ui/components/common/Loader';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import Button from '~/ui/components/common/Button';
import PageHeader from '~/ui/components/reusable/PageHeader';
import Header from './Header';
import Table from './Table';
import AddDeviceCsv from './Header/AddDeviceCsv';
import Actions from './popups/Actions';
import AssignDevicesModal from './popups/AssignDevices';
import ModalDescription from './ModalDescription';

import { INITIAL_ACTION } from './constants';
import { IIdName } from '~/services/api/types';
import { IDevicesListFilters } from '~/services/api/devices/types';
import { DeviceAction, IAction } from './types';
import styles from './Inventory.module.scss';
import { ISort } from '~/utils/sort/types';

const Inventory = (): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [programs, setPrograms] = useState<IIdName[]>([]);
  const [filters, setFilters] = useState<IDevicesListFilters>(null);
  const [sort, setSort] = useState<ISort>({} as ISort);
  const [checkedDevicesIds, setCheckedDevicesIds] = useState([]);
  const [action, setAction] = useState<IAction>(INITIAL_ACTION);
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const [isCsvAdding, setIsCsvAdding] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { items: devices, pagination } = useStoreState(state => state.devices);
  const { onGetDevices, onGetMoreDevices } = useStoreActions(actions => actions.devices);
  const { showNotify, showError } = useStoreActions(actions => actions.snackbar);

  const devicesActions = useMemo(
    () => getDevicesActions(devices, checkedDevicesIds),
    [checkedDevicesIds.length],
  );

  const onAction = (actionType: DeviceAction, devicesIds: number[]) => {
    setAction({ type: actionType, devicesIds });
  };

  const handleActionsBtnClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorElement(prev => (prev ? null : e.currentTarget));
  };

  const handleActionsClose = () => {
    setAnchorElement(null);
  };

  const resetAction = () => {
    setAction(INITIAL_ACTION);
  };

  const getDevices = async () => {
    setIsLoading(true);
    setCheckedDevicesIds([]);

    try {
      await onGetDevices({ ...filters, ...sort });
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const onLoadMoreDevices = async () => {
    try {
      await onGetMoreDevices({ ...filters, ...sort });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const onToggleCheckDevice = (deviceId: number) => {
    setCheckedDevicesIds(prev =>
      prev.includes(deviceId) ? prev.filter(id => id !== deviceId) : [...prev, deviceId],
    );
  };

  /* Add DevicesCSV */

  const handleAddDeviceCsv = async (formData: FormData) => {
    try {
      setIsCsvAdding(true);
      await api.devices.uploadDevices(formData);
      getDevices();
      showNotify('Devices successfully added');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsCsvAdding(false);
    }
  };

  /* Export Devices to xlsx */

  const handleExportDevices = async () => {
    try {
      setIsExporting(true);
      const { data, headers } = await api.devices.getDevicesReport(filters);
      const fileName = getFileNameFromContentDisposition(headers['content-disposition']);
      downloadFile(window.URL.createObjectURL(new Blob([data])), fileName);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsExporting(false);
    }
  };

  /* Assign to program  */

  const handleAssignDevices = async (programId: number) => {
    try {
      await api.devices.assignDevices(programId, action.devicesIds);
      getDevices();
      showNotify('Devices successfully assigned');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Unassign from program  */

  const handleUnAssignDevices = async () => {
    try {
      await api.devices.unAssignDevices(action.devicesIds);
      getDevices();
      showNotify('Devices successfully unassigned');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Unregister from patient */

  const handleUnRegisterDevices = async () => {
    try {
      await api.devices.unRegisterDevices(action.devicesIds);
      getDevices();
      showNotify('Devices successfully unregistered');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Mark as Returned */

  const handleMarkAsReturnedDevices = async () => {
    try {
      await api.devices.markAsReturnedDevices(action.devicesIds);
      getDevices();
      showNotify('Devices successfully marked as Returned');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Mark as Lost */

  const handleMarkAsLostDevices = async () => {
    try {
      await api.devices.markAsLostDevices(action.devicesIds);
      getDevices();
      showNotify('Devices successfully marked as Lost');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Mark as Defective */

  const handleMarkAsDefectiveDevices = async () => {
    try {
      await api.devices.markAsDefectiveDevices(action.devicesIds);
      getDevices();
      showNotify('Devices successfully marked as Defective');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Mark As Available  */

  const handleMarkAsAvailable = async () => {
    try {
      await api.devices.markAsAvailable(action.devicesIds);
      getDevices();
      showNotify('Devices successfully marked as Available');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Deactivate Sim */

  const handleDeactivateSim = async () => {
    try {
      await api.devices.deactivateSim(action.devicesIds);
      getDevices();
      showNotify('SIM successfully deactivated');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Activate Sim */

  const handleActivateSim = async () => {
    try {
      await api.devices.activateSim(action.devicesIds);
      getDevices();
      showNotify('SIM successfully activated');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
    }
  };

  /* Remove devices */

  const handleRemoveDevices = async () => {
    try {
      setIsDeleting(true);
      await Promise.all(action.devicesIds.map(deviceId => api.devices.deleteDevice(deviceId)));
      await getDevices();
      showNotify('Device removed');
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      resetAction();
      setCheckedDevicesIds([]);
      setIsDeleting(true);
    }
  };

  const onMount = async () => {
    setIsLoading(true);

    try {
      const selectors = await api.devices.getSelectors().then(res => res.data);
      setPrograms(selectors.programs);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getDevices();
  }, [filters, sort]);

  useEffect(() => {
    onMount();
  }, []);

  return (
    <>
      <PageHeader title="Inventory">
        <>
          <AddDeviceCsv isCsvAdding={isCsvAdding} onAddDeviceCsv={handleAddDeviceCsv} />
          <Button
            variant="contained"
            color="primary"
            endIcon={<ExpandMoreIcon />}
            onClick={handleActionsBtnClick}
          >
            {checkedDevicesIds.length > 0 && `(${checkedDevicesIds.length}) `}Actions
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isExporting}
            isLoading={isExporting}
            endIcon={<InsertDriveFileIcon />}
            onClick={handleExportDevices}
          >
            Export
          </Button>
          <Actions
            anchorElement={anchorElement}
            actions={devicesActions}
            allDevices
            onSelect={onAction}
            onClose={handleActionsClose}
          />
        </>
      </PageHeader>
      <Paper elevation={3} className={styles.container}>
        <Header programs={programs} onSubmit={setFilters} />
        <InfiniteScroll
          dataLength={devices.length}
          next={onLoadMoreDevices}
          hasMore={pagination.hasMore}
          loader={<Loader />}
        >
          <Table
            devices={devices}
            checkedDevicesIds={checkedDevicesIds}
            isLoading={isLoading}
            sort={sort}
            setSort={setSort}
            onToggleCheckDevice={onToggleCheckDevice}
            onAction={onAction}
          />
        </InfiniteScroll>
      </Paper>
      {action.type === DeviceAction.Assign && (
        <AssignDevicesModal
          programs={programs}
          description={
            <ModalDescription
              title="Selected devices:"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={handleAssignDevices}
        />
      )}
      {action.type === DeviceAction.Unassign && (
        <ConfirmModal
          title="Unassign devices"
          description={
            <ModalDescription
              title="Are you sure you want to unassign selected devices?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={handleUnAssignDevices}
        />
      )}
      {action.type === DeviceAction.Unregister && (
        <ConfirmModal
          title="Unregister devices"
          description={
            <ModalDescription
              title="Are you sure you want to unregister selected devices?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={handleUnRegisterDevices}
        />
      )}
      {action.type === DeviceAction.MarkAsReturned && (
        <ConfirmModal
          title="Mark devices as Returned"
          description={
            <ModalDescription
              title="Are you sure you want to mark selected devices as Returned?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={handleMarkAsReturnedDevices}
        />
      )}
      {action.type === DeviceAction.MarkAsLost && (
        <ConfirmModal
          title="Mark devices as Lost"
          description={
            <ModalDescription
              title="Are you sure you want to mark selected devices as Lost?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={handleMarkAsLostDevices}
        />
      )}
      {action.type === DeviceAction.MarkAsDefective && (
        <ConfirmModal
          title="Mark devices as Defective"
          description={
            <ModalDescription
              title="Are you sure you want to mark selected devices as Defective?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={handleMarkAsDefectiveDevices}
        />
      )}
      {action.type === DeviceAction.MarkAsAvailable && (
        <ConfirmModal
          title="Mark devices as Available"
          description={
            <ModalDescription
              title="Are you sure you want to mark selected devices as Available?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={handleMarkAsAvailable}
        />
      )}
      {action.type === DeviceAction.DeactivateSim && (
        <ConfirmModal
          title="Deactivate SIM"
          description={
            <ModalDescription
              title="Are you sure you want to deactivate SIM?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={() => handleDeactivateSim()}
        />
      )}
      {action.type === DeviceAction.ActivateSim && (
        <ConfirmModal
          title="Activate SIM"
          description={
            <ModalDescription
              title="Are you sure you want to Activate SIM?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          onClose={resetAction}
          onConfirm={() => handleActivateSim()}
        />
      )}
      {action.type === DeviceAction.Delete && (
        <ConfirmModal
          title="Delete"
          description={
            <ModalDescription
              title="Are you sure you want to delete next devices?"
              devices={devices}
              devicesIds={action.devicesIds}
            />
          }
          isLoading={isDeleting}
          onClose={resetAction}
          onConfirm={handleRemoveDevices}
        />
      )}
    </>
  );
};

export default Inventory;
